<script lang="ts">
  import type { FocusTarget } from '../app';
  import { lang, t } from '../i18n';
  import { focus, focusContext, focusOn } from '../state/stores';
  import Icon from './icons/Icon.svelte';

  let { targets }: { targets: Map<string, FocusTarget> } = $props();

  interface Crumb {
    id: string;
    label: string;
  }

  const crumbs = $derived.by((): Crumb[] => {
    const root: Crumb = { id: 'sun', label: $t('breadcrumb.root') };
    const id = $focus;
    if (id === 'sun') return [root];
    const target = targets.get(id);
    if (!target) return [root];
    const name = (x: FocusTarget): string => x.name[$lang];
    const list = [root];
    const context = target.kind === 'spacecraft' ? $focusContext : (target.parent ?? null);
    if (context) {
      const ctxTarget = targets.get(context);
      if (ctxTarget) list.push({ id: ctxTarget.id, label: name(ctxTarget) });
    }
    list.push({ id, label: name(target) });
    return list;
  });
</script>

<nav class="breadcrumb" aria-label={$t('breadcrumb.label')}>
  <ol>
    {#each crumbs as crumb, i (crumb.id)}
      <li>
        {#if i < crumbs.length - 1}
          <button type="button" class="link" onclick={() => focusOn(crumb.id)}>
            {#if crumb.id === 'sun'}
              <span class="root-icon"><Icon name="sun" size={20} /></span><span class="root-text">{crumb.label}</span>
            {:else}
              {crumb.label}
            {/if}
          </button>
          <span class="sep" aria-hidden="true">/</span>
        {:else}
          <span class="current" aria-current="location">{crumb.label}</span>
        {/if}
      </li>
    {/each}
  </ol>
</nav>

<style>
  .breadcrumb {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
  }
  ol {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    min-width: 0;
    margin: 0;
    padding: 0 6px;
    list-style: none;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
  }
  li {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .link {
    min-height: var(--touch);
    padding: 0 8px;
    display: flex;
    align-items: center;
    color: var(--fg-muted);
    letter-spacing: inherit;
    text-transform: inherit;
    transition: color var(--dur-fast) var(--ease);
  }
  .link:hover {
    color: var(--accent);
  }
  .current {
    padding: 0 8px;
    line-height: var(--touch);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sep {
    padding: 0 2px;
    color: var(--fg-muted);
    font-weight: 400;
  }
  .root-icon {
    display: none;
  }
  @media (max-width: 480px) {
    .root-icon {
      display: block;
    }
    .root-text {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
    }
    ol {
      padding: 0 2px;
      font-size: 0.75rem;
      letter-spacing: 0.08em;
    }
    .link,
    .current {
      padding: 0 4px;
    }
  }
</style>
